import { useCallback, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import {
  buildMediaIndex,
  mediaItemsFromIndex,
  parseMediaIndex,
  MEDIA_INDEX_KIND,
  MEDIA_INDEX_TAG,
} from '@/lib/mediaSync';
import { toast } from '@/hooks/useToast';

/**
 * Syncs the media *index* — a sha256-catalog of every file in the
 * library — to Nostr, NIP-44 encrypted to the user's own pubkey.
 *
 * **The design constraint is the whole point:** the media *bytes* stay
 * local-only on whatever device holds them, because local-only files are
 * genuinely local-only. What syncs is only the catalog — so a new device
 * can know the library exists (every file's sha256, tags, alt text, sizes,
 * Blossom URLs) and show it as "local only, need to re-add," without ever
 * pretending the files themselves sync over the wire.
 */
export function useMediaIndexSync() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { items, putSynced } = useMediaLibrary();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const [isSyncing, setIsSyncing] = useState(false);

  const canSync = Boolean(user?.signer.nip44);

  /** Push the current library catalog to Nostr. */
  const syncUp = useCallback(async () => {
    if (!user || !canSync) return;
    setIsSyncing(true);
    try {
      const doc = buildMediaIndex(items ?? []);
      const content = await user.signer.nip44!.encrypt(user.pubkey, JSON.stringify(doc));
      await publishEvent({
        kind: MEDIA_INDEX_KIND,
        content,
        tags: [['d', MEDIA_INDEX_TAG], ['t', MEDIA_INDEX_TAG]],
      });
    } finally {
      setIsSyncing(false);
    }
  }, [user, items, canSync, publishEvent]);

  /** Pull the media catalog and merge it into the local library. */
  const syncDown = useCallback(async (): Promise<number | undefined> => {
    if (!user || !canSync) return undefined;
    setIsSyncing(true);
    try {
      const signal = AbortSignal.timeout(8000);
      // The `authors` filter is load-bearing, not optional: the index is
      // an addressable event (kind 39653), so anyone can publish one under
      // the same d-tag. Without constraining to the current user, a
      // second account on the same device (or a stranger's event) would be
      // fetched and fail decryption at best — the query must only ever
      // return this account's own index.
      const events = await nostr.query(
        [{ kinds: [MEDIA_INDEX_KIND], authors: [user.pubkey], '#d': [MEDIA_INDEX_TAG], limit: 10 }],
        { signal },
      );

      // Latest index document by timestamp wins across devices.
      const valid = events
        .map((event) => {
          try {
            return event;
          } catch {
            return undefined;
          }
        })
        .filter((event): event is NonNullable<typeof event> => Boolean(event))
        .sort((a, b) => b.created_at - a.created_at);

      if (valid.length === 0) return 0;

      const content = await user.signer.nip44!.decrypt(user.pubkey, valid[0].content);
      const doc = parseMediaIndex(valid[0], content);
      if (!doc) return 0;

      const remoteItems = mediaItemsFromIndex(doc, user.pubkey);
      const localBySha = new Map((items ?? []).map((item) => [item.sha256, item]));
      let pulled = 0;

      for (const item of remoteItems) {
        const local = localBySha.get(item.sha256);
        // Merge: keep the copy with more information (uploads, alt, tags).
        // The remote never overwrites local bytes — "local only" is a
        // strict guarantee.
        if (!local) {
          putSynced(item);
          pulled += 1;
        } else if (item.uploads.length > local.uploads.length) {
          putSynced({ ...local, uploads: item.uploads, tags: item.tags, alt: item.alt ?? local.alt });
          pulled += 1;
        }
      }

      return pulled;
    } finally {
      setIsSyncing(false);
    }
  }, [user, items, canSync, nostr, putSynced]);

  return { isSyncing, syncUp, syncDown, canSync };
}
